import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";

import { changeBelegClaim, fetchBelege, fetchBelegSupplierOptions, fetchCreditDashboard, belegDetailHref, BELEG_DOC_TYPE_LABEL, BELEG_DOC_TYPE_LABELS, BELEG_DOC_TYPES_WITH_REVIEW } from "../api/belege";
import CreditDashboard from "../components/CreditDashboard";
import CostCenterFilter from "../components/CostCenterFilter";
import type {
  BelegDocType,
  BelegReviewFilter,
  BelegRow,
  BelegSupplierRow,
  BelegSort,
  BelegWorkflowStatus,
} from "../api/belege";
import AccountingPeriodControl, { type AccountingPeriodMode } from "../components/AccountingPeriodControl";
import { accountingRangeError, accountingRangeLabel, recentAccountingRange } from "../lib/accountingPeriod";
import EmptyState, { emptyStateActionClass } from "../components/EmptyState";
import BelegReviewStates from "../components/BelegReviewStates";
import {
  TableErrorRow,
  TableHeadCell,
  TableLoadMoreFooter,
  TableLoadingRow,
  TablePanel,
} from "../components/DataTable";
import DocumentPdfActions from "../components/DocumentPdfActions";
import FilterDropdown from "../components/FilterDropdown";
import FilterField from "../components/FilterField";
import SearchInput from "../components/SearchInput";
import SegmentedControl from "../components/SegmentedControl";
import { isDemoMode } from "../lib/env";
import { formatDate, formatEuro, toNumber } from "../lib/format";
import { reviewStateLabel } from "../lib/reviewStatusLabels";
import { useDebouncedValue } from "../lib/useDebouncedValue";

import { usePersonalView } from "../lib/usePersonalView";
import PersonalViewNotice, { PersonalViewReset } from "../components/PersonalViewNotice";
import SavedOfferSelection from "../components/SavedOfferSelection";
import { savedOfferDetailUrl, savedOfferIds } from "../lib/savedOfferNavigation";
import { useSession } from "../auth/useSession";
import type { BelegListFilters } from "../components/BelegNeighbours";

const PAGE_SIZE = 50;
const SUPPLIER_FILTER_LIMIT = 6;

type SupplierOption = {
  supplier: string;
  count: number;
  amount: number;
};

type DocTypeFilter = "all" | BelegDocType;
type ReviewFilter = "all" | BelegReviewFilter;
type WorkflowTab = "all" | Exclude<BelegWorkflowStatus, "none">;
type CommentFilter = "all" | "with";
type FilterParamKey =
  | "q"
  | "supplier"
  | "type"
  | "status"
  | "review"
  | "comments"
  | "sort"
  | "mine"
  | "assignment";

// Kind pills in the order of the purchasing process (#546).
const DOC_TYPE_OPTIONS: Array<{ value: DocTypeFilter; label: string }> = [
  { value: "all", label: "Alle" },
  { value: "angebot", label: BELEG_DOC_TYPE_LABELS.angebot },
  { value: "einzelauftrag", label: BELEG_DOC_TYPE_LABELS.einzelauftrag },
  { value: "rechnung", label: BELEG_DOC_TYPE_LABELS.rechnung },
  { value: "gutschrift", label: BELEG_DOC_TYPE_LABELS.gutschrift },
];

const WORKFLOW_TABS: Array<{ value: WorkflowTab; label: string }> = [
  { value: "open", label: "Zu prüfen" },
  { value: "in_review", label: "In Klärung" },
  { value: "resolved", label: "Erledigt" },
  { value: "all", label: "Alle" },
];

const REVIEW_OPTIONS: Array<{ value: ReviewFilter; label: string }> = [
  { value: "all", label: "Alle" },
  { value: "open", label: reviewStateLabel("open") },
  { value: "complaint", label: reviewStateLabel("complaint") },
  { value: "credit_expected", label: reviewStateLabel("credit_expected") },
  { value: "write_off", label: reviewStateLabel("write_off") },
  { value: "credit_resolved", label: reviewStateLabel("credit_resolved") },
  { value: "unspecified", label: "Nicht näher zugeordnet" },
];

function parseWorkflowTab(value: string | null, legacyReview: boolean, docType: DocTypeFilter): WorkflowTab {
  if (WORKFLOW_TABS.some(option => option.value === value)) return value as WorkflowTab;
  // Existing links with a price-review filter keep their original scope.
  if (legacyReview || docType === "angebot") return "all";
  return "open";
}

function parseConfirmationStatus(value: string | null): NonNullable<BelegRow["confirmation_status"]> | null {
  return value === "open" || value === "query_required" || value === "approved" || value === "rejected" ? value : null;
}

const COMMENT_OPTIONS: Array<{ value: CommentFilter; label: string }> = [
  { value: "all", label: "Alle" },
  { value: "with", label: "Mit Kommentar" },
];

function parseDocTypeFilter(value: string | null): DocTypeFilter {
  return DOC_TYPE_OPTIONS.some((option) => option.value === value)
    ? (value as DocTypeFilter)
    : "all";
}

// Accounting opens on the work that still needs a decision; "all" is explicit (#533).
const DEFAULT_REVIEW_FILTER: ReviewFilter = "all";

function parseReviewFilter(value: string | null): ReviewFilter {
  return REVIEW_OPTIONS.some((option) => option.value === value)
    ? (value as ReviewFilter)
    : DEFAULT_REVIEW_FILTER;
}

function parseCommentFilter(value: string | null): CommentFilter {
  return COMMENT_OPTIONS.some((option) => option.value === value)
    ? (value as CommentFilter)
    : "all";
}

function parseBelegSort(value: string | null): BelegSort {
  return value === "open_overpay_desc" || value === "open_overpay_asc"
    ? value
    : "recent_desc";
}

function setFilterParamValue(
  params: URLSearchParams,
  key: FilterParamKey,
  value: string | null,
): void {
  const cleanValue = value?.trim() ?? "";
  if (cleanValue) {
    params.set(key, cleanValue);
  } else {
    params.delete(key);
  }
}

// Zwei Seiten aus einer (#630): „Belege“ ist das Archiv zum Nachschlagen,
// „Rechnungsprüfung“ die Arbeitsliste für Rechnungen und Gutschriften.
export type BelegePageMode = "archive" | "review";
const REVIEW_DOC_TYPES: DocTypeFilter[] = ["rechnung", "gutschrift"];

export default function BelegePage({ mode = "archive" }: { mode?: BelegePageMode }) {
  const review = mode === "review";
  const location = useLocation();
  const user = useSession().user;
  const membership = user?.active_membership;
  const queryClient = useQueryClient();
  // Die Rechnungsprüfung öffnet immer auf der offenen Arbeit, ohne gemerkte Filter.
  const personalView = usePersonalView("belege", { persist: !review });
  const { params: searchParams, setUrl: setSearchParams, change: changeView } = personalView;
  const from = searchParams.get("from") ?? "";
  const to = searchParams.get("to") ?? "";
  const hasPeriod = searchParams.has("from") || searchParams.has("to") || searchParams.get("period") === "custom";
  const periodMode: AccountingPeriodMode = !hasPeriod ? "all" : searchParams.get("period") === "1m" ? "1m" : searchParams.get("period") === "1y" ? "1y" : "custom";
  const periodError = hasPeriod ? accountingRangeError(from, to) : null;
  function changePeriod(mode: AccountingPeriodMode, range = recentAccountingRange(mode === "1y" ? 12 : 1)) {
    const next = new URLSearchParams(searchParams);
    for (const key of ["period", "from", "to"]) next.delete(key);
    if (mode !== "all") {
      next.set("period", mode); next.set("from", range.from); next.set("to", range.to);
    }
    changeView(next);
  }
  const urlSearch = searchParams.get("q") ?? "";
  const [search, setSearch] = useState(urlSearch);
  const supplierFilter = searchParams.get("supplier");
  const costCenterType = searchParams.get("cost_center_type");
  const costCenterKey = searchParams.get("cost_center_key");
  const costCenterSelection = costCenterType && costCenterKey
    ? { type: costCenterType, key: costCenterKey } : null;
  const parsedDocType = parseDocTypeFilter(searchParams.get("type"));
  const docTypeFilter: DocTypeFilter = review
    ? (REVIEW_DOC_TYPES.includes(parsedDocType) ? parsedDocType : "rechnung")
    : parsedDocType;
  // Ältere Links mit ?review=… wählen den passenden Reiter; einen unsichtbaren
  // Preisentscheidungs-Filter gibt es nicht mehr (#630).
  const legacyReviewTab: WorkflowTab | null = (() => {
    const value = parseReviewFilter(searchParams.get("review"));
    if (!searchParams.has("review") || searchParams.has("status")) return null;
    if (value === "open") return "open";
    if (value === "complaint" || value === "credit_expected") return "in_review";
    if (value === "write_off" || value === "credit_resolved") return "resolved";
    return "all";
  })();
  const workflowTab: WorkflowTab = review
    ? legacyReviewTab ?? parseWorkflowTab(searchParams.get("status"), false, docTypeFilter)
    : "all";
  // Ein gezielter Prüfzustand (z. B. „Gutschrift erwartet“ aus der
  // Gutschriften-Übersicht) bleibt als sichtbarer, entfernbarer Chip (#630).
  const parsedReviewFilter = parseReviewFilter(searchParams.get("review"));
  const reviewFilter: ReviewFilter = review && parsedReviewFilter !== "open" ? parsedReviewFilter : "all";
  const confirmationStatus = parseConfirmationStatus(searchParams.get("ab_status"));
  const commentFilter: CommentFilter = review ? parseCommentFilter(searchParams.get("comments")) : "all";
  const sort = parseBelegSort(searchParams.get("sort"));
  const mine = review && searchParams.get("mine") === "1";
  const costCenterOpen = searchParams.get("assignment") === "open" && docTypeFilter === "gutschrift";
  const selectedOffers = savedOfferIds(searchParams);
  const debouncedSearch = useDebouncedValue(search, 200);
  const debouncedSearchTerm = debouncedSearch.trim();
  const belegListFilters: BelegListFilters = {
    from: from || undefined,
    to: to || undefined,
    q: debouncedSearchTerm || undefined,
    supplier: supplierFilter,
    costCenterType,
    costCenterKey,
    docType: docTypeFilter === "all" ? null : docTypeFilter,
    workflowStatus: workflowTab === "all" ? null : workflowTab,
    confirmationStatus: docTypeFilter === "einzelauftrag" ? confirmationStatus : null,
    reviewState: reviewFilter === "all" ? null : reviewFilter,
    withComments: commentFilter === "with",
    mine,
    costCenterOpen,
    sort,
  };
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const suppressSearchParamSyncRef = useRef(false);
  const lastUrlSearchRef = useRef(urlSearch);
  const currentPath = `${location.pathname}${location.search}`;

  const updateFilterParam = useCallback(
    (
      key: FilterParamKey,
      value: string | null,
      options?: { replace?: boolean },
    ) => {
      const next = new URLSearchParams(searchParams);
      setFilterParamValue(next, key, value);
      if (next.toString() !== searchParams.toString()) {
        changeView(next, options);
      }
    },
    [searchParams, changeView],
  );

  useEffect(() => {
    if (lastUrlSearchRef.current === urlSearch) return;
    lastUrlSearchRef.current = urlSearch;
    suppressSearchParamSyncRef.current = true;
    setSearch(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    if (suppressSearchParamSyncRef.current) {
      suppressSearchParamSyncRef.current = false;
      return;
    }
    if (debouncedSearchTerm !== search.trim()) return;
    updateFilterParam("q", debouncedSearchTerm, { replace: true });
  }, [debouncedSearchTerm, search, updateFilterParam]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const typeParam = searchParams.get("type");
    const reviewParam = searchParams.get("review");
    const statusParam = searchParams.get("status");
    const commentsParam = searchParams.get("comments");
    const sortParam = searchParams.get("sort");
    if (typeParam !== null && docTypeFilter === "all") {
      next.delete("type");
    }
    if (reviewParam !== null && reviewFilter === DEFAULT_REVIEW_FILTER) {
      // Legacy review=all links mean all document states until a new tab is chosen.
      if (statusParam !== null) next.delete("review");
    }
    if (statusParam !== null && !WORKFLOW_TABS.some(option => option.value === statusParam)) next.delete("status");
    if (commentsParam !== null && commentFilter === "all") {
      next.delete("comments");
    }
    if (sortParam !== null && sort === "recent_desc") {
      next.delete("sort");
    }
    if (next.toString() !== searchParams.toString()) {
      if (!["q", "supplier", "type", "status", "review", "comments", "sort", "mine", "cost_center_type", "cost_center_key", "period", "from", "to"].some(key => next.has(key))) next.set("view", "default");
      setSearchParams(next, { replace: true });
    }
  }, [
    commentFilter,
    docTypeFilter,
    reviewFilter,
    searchParams,
    setSearchParams,
    sort,
  ]);

  const setSupplierFilter = useCallback(
    (next: string | null) => updateFilterParam("supplier", next),
    [updateFilterParam],
  );
  const setCostCenterFilter = useCallback(
    (nextSelection: { type: string; key: string } | null) => {
      const next = new URLSearchParams(searchParams);
      if (nextSelection) {
        next.set("cost_center_type", nextSelection.type);
        next.set("cost_center_key", nextSelection.key);
      } else {
        next.delete("cost_center_type");
        next.delete("cost_center_key");
      }
      changeView(next);
    },
    [searchParams, changeView],
  );
  const setDocTypeFilter = useCallback(
    (value: DocTypeFilter) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          setFilterParamValue(next, "type", value === "all" ? null : value);
          // Offers and order confirmations have no review yet; a review filter
          // would leave the list empty without saying why (#546).
          if (value !== "all" && !BELEG_DOC_TYPES_WITH_REVIEW.includes(value)) next.delete("review");
          if (value === "angebot") next.set("status", "all");
          if (value !== "gutschrift") next.delete("assignment");
          next.delete("ab_status");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const setWorkflowTab = useCallback(
    (value: WorkflowTab) => {
      const next = new URLSearchParams(searchParams);
      next.set("status", value);
      changeView(next);
    },
    [searchParams, changeView],
  );
  const setReviewFilter = useCallback(
    (value: ReviewFilter) => {
      const next = new URLSearchParams(searchParams);
      setFilterParamValue(next, "review", value === DEFAULT_REVIEW_FILTER ? null : value);
      if (value !== "all" || workflowTab === "all") next.set("status", "all");
      changeView(next);
    },
    [searchParams, changeView, workflowTab],
  );
  const setCommentFilter = useCallback(
    (next: CommentFilter) =>
      updateFilterParam("comments", next === "all" ? null : next),
    [updateFilterParam],
  );
  const setSort = useCallback(
    (next: BelegSort) =>
      updateFilterParam("sort", next === "recent_desc" ? null : next),
    [updateFilterParam],
  );
  const setMine = useCallback(
    (next: boolean) => updateFilterParam("mine", next ? "1" : null),
    [updateFilterParam],
  );
  const claimMutation = useMutation({
    mutationFn: ({ belegId, action }: { belegId: string; action: "claim" | "release" }) =>
      changeBelegClaim(belegId, action),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["belege"] });
    },
  });
  const setSelectedOffers = (ids: string[]) => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.delete("angebot");
      [...new Set(ids)].slice(0, 3).forEach(id => next.append("angebot", id));
      return next;
    }, { replace: true });
  };

  const belegeQuery = useInfiniteQuery({
    queryKey: [
      "belege",
      "list",
      {
        from, to,
        q: debouncedSearchTerm,
        supplierFilter,
        costCenterType,
        costCenterKey,
        docTypeFilter,
        workflowTab,
        confirmationStatus,
        reviewFilter,
        commentFilter,
        mine,
        costCenterOpen,
        sort,
      },
    ],
    queryFn: ({ pageParam }) =>
      fetchBelege({ ...belegListFilters, limit: PAGE_SIZE, offset: pageParam }),
    enabled: personalView.ready && !periodError,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length;
      if (lastPage.items.length < lastPage.limit) return undefined;
      if (nextOffset >= lastPage.total) return undefined;
      return nextOffset;
    },
    staleTime: 60_000,
  });

  const supplierQuery = useQuery({
    queryKey: ["belege", "supplier-options"],
    queryFn: fetchBelegSupplierOptions,
    staleTime: 300_000,
  });

  const refreshing = !personalView.ready || (!periodError && (belegeQuery.isLoading || search.trim() !== debouncedSearchTerm || (belegeQuery.isFetching && !belegeQuery.isFetchingNextPage)));
  const hasOtherSelection = Boolean(search.trim() || supplierFilter || costCenterSelection || docTypeFilter !== (review ? "rechnung" : "all") || commentFilter !== "all" || reviewFilter !== "all" || mine || costCenterOpen);
  const hasSelection = hasOtherSelection || (workflowTab !== "open" && workflowTab !== "all");
  const nothingToReview = workflowTab === "open" && !hasOtherSelection;
  function clearSelection() {
    setSearch("");
    const next = new URLSearchParams(searchParams);
    for (const key of ["q", "supplier", "type", "status", "review", "comments", "ab_status", "mine", "assignment", "cost_center_type", "cost_center_key"]) next.delete(key);
    changeView(next);
  }
  const belege = useMemo(
    () => refreshing || periodError || belegeQuery.isError ? [] : belegeQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [belegeQuery.data, refreshing, periodError, belegeQuery.isError],
  );
  const belegList = useMemo(() => belege.map((item) => item.beleg_id), [belege]);
  const belegListOpen = belege.length > 0 && belege.every((item) => (item.workflow_status ?? item.review_status) === "open");
  const firstPage = refreshing || periodError || belegeQuery.isError ? undefined : belegeQuery.data?.pages[0];
  const total = firstPage?.total ?? 0;
  const docTypeCounts = firstPage?.doc_type_counts ?? ({} as Record<BelegDocType, number>);
  const workflowCounts = firstPage?.workflow_status_counts;
  // Offers and order confirmations carry no price review yet (#546, Schritt 2).
  const kindHasReview = docTypeFilter === "all" || BELEG_DOC_TYPES_WITH_REVIEW.includes(docTypeFilter);
  // Die Nachverfolgung gehört zur Belegart Gutschriften (#539).
  const showCredits = review && docTypeFilter === "gutschrift";
  const creditDashboardQuery = useQuery({
    queryKey: ["belege", "credits"],
    queryFn: () => fetchCreditDashboard(),
    enabled: showCredits,
    retry: false,
    staleTime: 60_000,
  });
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = belegeQuery;
  const hasMoreBelege = Boolean(hasNextPage);
  const loadMoreBelege = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!hasMoreBelege) return;
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreBelege();
        }
      },
      { rootMargin: "280px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMoreBelege, loadMoreBelege]);

  const supplierOptions = useMemo(
    () => topSupplierOptions(supplierQuery.data?.items ?? []),
    [supplierQuery.data],
  );
  const showDemoCreditExamplesNote =
    isDemoMode() && searchParams.get("review") === "credit_expected";

  // Schlanke Tabelle (#630): Typ nur ohne Belegart-Filter, Prüfspalten nur in der Rechnungsprüfung.
  const showTypeColumn = !review && docTypeFilter === "all";
  const columnCount = 5 + (showTypeColumn ? 1 : 0) + (review ? 2 : 0);

  // Alte Prüf-Links (?review=…) gehören zur Rechnungsprüfung; ein gemerkter
  // Prüfstand im Archiv wird dagegen einfach ignoriert.
  if (!review && new URLSearchParams(location.search).has("review")) return <Navigate to={`/rechnungspruefung${location.search}`} replace />;

  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{review ? "Rechnungsprüfung" : "Belege"}</h1>
        {review ? null : <PersonalViewReset view={personalView} defaultLabel="Alle Belege, neueste zuerst" className="ml-auto" />}
        {review && !isDemoMode() && membership?.can_review_prices ? (
          <div className="flex rounded-md border border-slate-300 bg-white p-0.5 text-sm shadow-sm" role="group" aria-label="Zuständigkeit">
            {[{ value: false, label: "Alle" }, { value: true, label: "Meine" }].map(option => (
              <button key={option.label} type="button" aria-pressed={mine === option.value}
                onClick={() => setMine(option.value)}
                className={`rounded px-3 py-1 font-medium ${mine === option.value ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>
                {option.label}
              </button>
            ))}
          </div>
        ) : null}
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2" role="group" aria-label="Belegart">
        {DOC_TYPE_OPTIONS.filter(option => !review || REVIEW_DOC_TYPES.includes(option.value)).map((option) => {
          const count =
            option.value === "all"
              ? Object.values(docTypeCounts).reduce((sum, value) => sum + value, 0)
              : docTypeCounts[option.value] ?? 0;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={docTypeFilter === option.value}
              onClick={() => setDocTypeFilter(option.value)}
              className={`h-9 rounded-full border px-3.5 text-sm ${
                docTypeFilter === option.value
                  ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-900"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {option.label}
              <span className="ml-1.5 text-slate-500">{belegeQuery.isSuccess ? count.toLocaleString("de-AT") : "…"}</span>
            </button>
          );
        })}
      </div>
      {review ? <div className="mb-4 flex flex-wrap items-end gap-x-4 gap-y-2 border-b border-slate-200">
        <div className="flex flex-wrap gap-x-5 gap-y-0" role="group" aria-label="Prüfstand">
          {WORKFLOW_TABS.map(option => {
            const count = option.value === "all"
              ? Object.values(workflowCounts ?? {}).reduce((sum, value) => sum + value, 0)
              : workflowCounts?.[option.value] ?? 0;
            return <button key={option.value} type="button" aria-pressed={workflowTab === option.value}
              onClick={() => setWorkflowTab(option.value)}
              className={`-mb-px h-10 shrink-0 border-b-2 px-0.5 text-sm ${workflowTab === option.value
                ? "border-emerald-700 font-medium text-emerald-900"
                : "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-900"}`}>
              {option.label}<span className="ml-1.5 tabular-nums text-slate-500">{workflowCounts ? count.toLocaleString("de-AT") : "…"}</span>
            </button>;
          })}
        </div>
        {docTypeFilter === "gutschrift" && membership?.can_manage_cost_centers ? (
          <button type="button" aria-pressed={costCenterOpen} onClick={() => {
            const next = new URLSearchParams(searchParams);
            setFilterParamValue(next, "assignment", costCenterOpen ? null : "open");
            if (!costCenterOpen) next.set("status", "all");
            changeView(next);
          }} className={`mb-1 h-9 rounded-md border px-3 text-sm ${costCenterOpen ? "border-amber-500 bg-amber-50 font-medium text-amber-900" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
            Offene Kostenstellen-Zuordnungen
          </button>
        ) : null}
      </div> : null}
      {review && reviewFilter !== "all" ? (
        <div className="-mt-2 mb-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600">Nur:</span>
          <button type="button" onClick={() => setReviewFilter("all")} aria-label={`Filter ${REVIEW_OPTIONS.find(option => option.value === reviewFilter)?.label ?? reviewFilter} entfernen`}
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-emerald-600 bg-emerald-50 px-3 font-medium text-emerald-900 hover:bg-emerald-100">
            {REVIEW_OPTIONS.find(option => option.value === reviewFilter)?.label ?? reviewFilter}
            <span aria-hidden="true">×</span>
          </button>
        </div>
      ) : null}
      {!review && !kindHasReview ? (
        <p className="mb-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {docTypeFilter === "angebot" ? <>
            Angebote haben noch keinen gespeicherten Entscheidungsstatus. Du findest sie unter „Alle“ und kannst sie im Detail vorläufig prüfen.
          </> : <>
            Auftragsbestätigungen prüfst du im Detail gegen frühere Preise und kannst dort eine Rückfrage stellen oder entscheiden.
          </>}
        </p>
      ) : null}
      {!review && docTypeFilter === "angebot" && selectedOffers.length ? <>
        {selectedOffers.length ? <SavedOfferSelection selected={selectedOffers} rows={[]}
          onRemove={id => setSelectedOffers(selectedOffers.filter(value => value !== id))}
          onClear={() => setSelectedOffers([])} /> :
          <p className="mb-4 text-sm text-slate-600">Für einen gemeinsamen Vergleich markierst du zwei oder drei Angebote in der Liste.</p>}
      </> : null}
      <PersonalViewNotice view={personalView} defaultLabel="Zu prüfen, neueste zuerst" />
      {/* Keine Kennzahlenleiste mehr (#630): Mehrkosten und Ersparnis stehen in der Analyse. */}


      <section
        aria-label="Belegfilter"
        className="mb-4 grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(10rem,1fr)_minmax(16rem,1.6fr)_minmax(10rem,1fr)_minmax(14rem,1.2fr)]"
      >
        <div className="min-w-0">
          {/* The dropdown already names the period; only a custom range needs the text. */}
          <AccountingPeriodControl mode={periodMode} from={from} to={to} allowAll
            customOpen={periodMode === "custom"} error={periodError}
            label={periodError ? "Zeitraum noch nicht gültig" : periodMode === "custom" ? accountingRangeLabel(from, to) : ""}
            onModeChange={mode => changePeriod(mode, mode === "custom" && from && to ? { from, to } : undefined)}
            onFromChange={value => changePeriod("custom", { from: value, to })}
            onToChange={value => changePeriod("custom", { from, to: value })} />
          {hasPeriod ? <p className="mt-1 text-xs text-slate-500">Belege ohne Datum sind nicht enthalten.</p> : null}
        </div>
        <FilterField label="Suche">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Beleg, ER-Nummer, Großhändler oder Besteller …"
            ariaLabel="Beleg suchen"
            className="max-w-none"
          />
        </FilterField>
        <FilterField label="Kostenstelle">
          <CostCenterFilter value={costCenterSelection} onChange={setCostCenterFilter} />
        </FilterField>
        <FilterField label="Großhändler">
          <SupplierTabs
            value={supplierFilter}
            options={supplierOptions}
            onChange={setSupplierFilter}
          />
        </FilterField>
        {false ? (
        <FilterField
          label="Preisentscheidung"
          help={
            <div className="space-y-1">
              <p>
                <span className="font-medium">Klärung angefragt:</span> Eine
                Rückfrage läuft.
              </p>
              <p>
                <span className="font-medium">Gutschrift erwartet:</span> Eine
                Gutschrift wurde angefordert oder zugesagt.
              </p>
            </div>
          }
        >
          <FilterDropdown<ReviewFilter>
            value={reviewFilter}
            options={REVIEW_OPTIONS}
            onChange={setReviewFilter}
            ariaLabel="Preisentscheidung filtern"
            label="Preisentscheidung"
            defaultValue={DEFAULT_REVIEW_FILTER}
            layout="field"
          />
        </FilterField>
        ) : null}
        {false ? (
        <FilterField label="Kommentare">
          <FilterDropdown<CommentFilter>
            value={commentFilter}
            options={COMMENT_OPTIONS}
            onChange={setCommentFilter}
            ariaLabel="Kommentare filtern"
            label="Kommentare"
            defaultValue="all"
            layout="field"
          />
        </FilterField>
        ) : null}
      </section>

      {showDemoCreditExamplesNote ? (
        <aside
          data-testid="demo-credit-examples-note"
          className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-600"
        >
          <span className="font-medium text-slate-700">Demo-Auswahl:</span>{" "}
          Diese Liste zeigt bis zu zwei ausgewählte synthetische Beispiele. Der
          gesamte Bestand des Demo-Arbeitsbereichs kann höher sein.
        </aside>
      ) : null}

      {/* Eine erwartete Gutschrift ist noch keine: Sie steht über der Liste der
          eingegangenen, damit beides an einem Ort bleibt (#539). */}
      {showCredits ? (
        <div className="mb-5">
          <CreditDashboard
            ownPeriodControl={false}
            data={creditDashboardQuery.data}
            isLoading={creditDashboardQuery.isLoading}
            isError={creditDashboardQuery.isError}
            onRetry={() => {
              void creditDashboardQuery.refetch();
            }}
          />
        </div>
      ) : null}

      <TablePanel
        title={hasPeriod ? "Belege im Zeitraum" : "Letzte Belege"}
        footer={
          refreshing || periodError || belegeQuery.isError ? null : (
            <TableLoadMoreFooter
              loadMoreRef={loadMoreRef}
              loadedCount={belege.length}
              totalCount={total}
              itemLabel="Belegen"
              moreItemLabel="Belege"
              hasMore={hasMoreBelege}
              isLoading={isFetchingNextPage}
              buttonLabel="Weitere Belege laden"
              loadingLabel="Lade weitere Belege …"
              onLoadMore={loadMoreBelege}
            />
          )
        }
      >
        {claimMutation.isError ? (
          <div role="alert" className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
            Die Zuweisung konnte nicht geändert werden. Der Beleg ist möglicherweise schon einer anderen Person zugewiesen.
          </div>
        ) : null}
        {periodError ? <div className="p-4 text-sm text-slate-600">Korrigiere den Zeitraum oben oder <button type="button" className="font-medium text-emerald-700 underline" onClick={() => changePeriod("all")}>zeige alle Belege</button>.</div> : !refreshing && !belegeQuery.isError && belege.length === 0 ? (
            <div className="px-4 py-3">
              {nothingToReview ? <EmptyState
                title={hasPeriod ? "In diesem Zeitraum ist nichts zu prüfen" : "Alles geprüft"}
                actions={<button type="button" className={emptyStateActionClass} onClick={() => setWorkflowTab("all")}>Alle Belege anzeigen</button>}
              >
                Kein Beleg wartet auf eine Prüfung. Bereits entschiedene Belege und Angebote findest du unter „Alle“.
              </EmptyState> : <EmptyState
                title={hasSelection ? "Keine passenden Belege gefunden" : hasPeriod ? "Keine Belege in diesem Zeitraum" : "Noch keine Belege vorhanden"}
                actions={<>{hasPeriod ? <button type="button" className={emptyStateActionClass} onClick={() => changePeriod("all")}>Zeitraum aufheben</button> : null}{workflowTab === "open" && search.trim() ? <button type="button" className={emptyStateActionClass} onClick={() => setWorkflowTab("all")}>In allen Belegen suchen</button> : null}{hasSelection ? (
                  <button type="button" className={emptyStateActionClass} onClick={clearSelection}>
                    Suche und Filter zurücksetzen
                  </button>
                ) : (
                  <Link to="/imports" className={emptyStateActionClass}>Importe öffnen</Link>
                )}</>}
              >
                {hasSelection
                  ? <>Die Suche und die gesetzten Filter liefern keine Treffer. Gesucht wird nach Belegnummer, Großhändler und Besteller; Artikelbeschreibungen findest du in der <Link className="font-medium text-emerald-800 underline underline-offset-2" to={`/articles${urlSearch.trim() ? `?${new URLSearchParams({q: urlSearch.trim()})}` : ""}`}>Artikelsuche</Link>.</>
                  : hasPeriod ? "Für diesen Zeitraum sind keine passenden Belege vorhanden. Du kannst oben andere Daten wählen oder den Zeitraum aufheben; die übrigen Filter bleiben erhalten." : "Hier erscheinen eingelesene Angebote, Auftragsbestätigungen, Rechnungen und Gutschriften. Unter Importe kannst du den Verarbeitungsstand deiner Unterlagen ansehen."}
              </EmptyState>}
            </div>
          ) : (
          <table className="w-full min-w-[860px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <TableHeadCell>Datum</TableHeadCell>
              <TableHeadCell>Beleg</TableHeadCell>
              {showTypeColumn ? <TableHeadCell>Typ</TableHeadCell> : null}
              <TableHeadCell>Großhändler</TableHeadCell>
              <TableHeadCell>Besteller</TableHeadCell>
              <TableHeadCell>Kostenstelle</TableHeadCell>
              {review ? <TableHeadCell>Prüfstand</TableHeadCell> : null}
              {review ? <OpenOverpayHeadCell sort={sort} onChange={setSort} /> : null}
              <TableHeadCell align="right">Betrag</TableHeadCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {refreshing ? (
              <TableLoadingRow colSpan={columnCount} label="Lade Belege …" />
            ) : null}
            {!refreshing && belegeQuery.isError ? (
              <TableErrorRow colSpan={columnCount}>
                Belege konnten nicht geladen werden.
              </TableErrorRow>
            ) : null}
            {!refreshing && !belegeQuery.isError
              ? belege.map((beleg) => (
                  <BelegTableRow
                    key={beleg.beleg_id}
                    beleg={beleg}
                    fromPath={currentPath}
                    selectedOffers={selectedOffers}
                    // The order of the loaded list travels with the document
                    // so Controlling can work through it (#546).
                    belegList={belegList}
                    belegListOpen={belegListOpen}
                    belegListTotal={total}
                    belegListFilters={belegListFilters}
                    showPriceReview={review}
                    showType={showTypeColumn}
                    showOfferSelection={false}
                    offerSelected={selectedOffers.includes(beleg.beleg_id)}
                    offerSelectionFull={selectedOffers.length >= 3}
                    onOfferToggle={checked => setSelectedOffers(checked
                      ? [...selectedOffers, beleg.beleg_id]
                      : selectedOffers.filter(id => id !== beleg.beleg_id))}
                    currentUserId={user?.user_id ?? null}
                    canClaim={!isDemoMode() && membership?.can_review_prices === true}
                    canManageCostCenters={membership?.can_manage_cost_centers === true}
                    claimPending={claimMutation.isPending && claimMutation.variables?.belegId === beleg.beleg_id}
                    onClaim={action => claimMutation.mutate({ belegId: beleg.beleg_id, action })}
                  />
                ))
              : null}
          </tbody>
        </table>
          )}
      </TablePanel>
    </main>
  );
}

function SupplierTabs({
  value,
  options,
  onChange,
}: {
  value: string | null;
  options: SupplierOption[];
  onChange: (next: string | null) => void;
}) {
  const allValue = "__all__";
  return (
    <SegmentedControl<string>
      value={value ?? allValue}
      options={[
        { value: allValue, label: "Alle" },
        ...options.map((option) => ({
          value: option.supplier,
          label: option.supplier,
          title: `${option.count.toLocaleString("de-AT")} Belege · ${formatEuro(
            option.amount,
          )}`,
        })),
      ]}
      onChange={(next) => onChange(next === allValue ? null : next)}
      ariaLabel="Großhändler filtern"
    />
  );
}

function BelegTableRow({
  beleg,
  fromPath,
  selectedOffers,
  belegList,
  belegListOpen,
  belegListTotal,
  belegListFilters,
  showPriceReview,
  showType,
  showOfferSelection,
  offerSelected,
  offerSelectionFull,
  onOfferToggle,
  currentUserId,
  canClaim,
  canManageCostCenters,
  claimPending,
  onClaim,
}: {
  beleg: BelegRow;
  fromPath: string;
  selectedOffers: string[];
  belegList: string[];
  belegListOpen: boolean;
  belegListTotal: number;
  belegListFilters: BelegListFilters;
  showPriceReview: boolean;
  showType: boolean;
  showOfferSelection: boolean;
  offerSelected: boolean;
  offerSelectionFull: boolean;
  onOfferToggle: (checked: boolean) => void;
  currentUserId: string | null;
  canClaim: boolean;
  canManageCostCenters: boolean;
  claimPending: boolean;
  onClaim: (action: "claim" | "release") => void;
}) {
  const buyer = beleg.buyer_name ?? beleg.buyer_email;
  const docLabel = beleg.doc_number ?? beleg.beleg_id;
  const missingContext = toNumber(beleg.missing_cost_center_line_count) ?? 0;
  const commentCount = toNumber(beleg.review_comment_count) ?? 0;
  const openOverpay = toNumber(beleg.open_overpay_abs);
  const claimedBySelf = Boolean(currentUserId && beleg.claim_user_id === currentUserId);
  const detailHref = beleg.doc_type === "angebot"
    ? savedOfferDetailUrl(beleg.beleg_id, selectedOffers, fromPath)
    : belegDetailHref(beleg.beleg_id);

  return (
    <tr className="hover:bg-slate-50">
      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-slate-700">
        {formatDate(beleg.doc_date)}
      </td>
      <td className="px-3 py-1.5">
        <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
          {showOfferSelection && beleg.doc_type === "angebot" ? <input type="checkbox"
            aria-label={`Angebot ${docLabel} vergleichen`} checked={offerSelected}
            disabled={!offerSelected && offerSelectionFull}
            onChange={event => onOfferToggle(event.target.checked)} /> : null}
          {beleg.pdf_available ? (
            <DocumentPdfActions
              docId={beleg.pdf_doc_id}
              docNumber={docLabel}
              linePos={null}
            />
          ) : (
            <span className="font-mono text-xs text-slate-600">{docLabel}</span>
          )}
          <Link
            to={detailHref}
            state={{ from: fromPath, belegList, belegListOpen, belegListTotal, belegListFilters }}
            aria-label={`Details ${docLabel}`}
            className="shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          >
            Details
          </Link>
        </div>
        {beleg.external_number ? (
          <span className="mt-0.5 block truncate text-xs text-slate-600" title={`ER-Nummer ${beleg.external_number}`}>
            ER {beleg.external_number}
          </span>
        ) : null}
      </td>
      {showType ? <td className="px-3 py-1.5">
        <DocTypeBadge value={beleg.doc_type} />
      </td> : null}
      <td className="px-3 py-1.5 text-slate-700">{beleg.supplier ?? "–"}</td>
      <td className="px-3 py-1.5">
        <span className="block truncate text-slate-700" title={buyer ?? undefined}>
          {buyer ?? "–"}
        </span>
      </td>
      <td className="px-3 py-1.5">
        {missingContext > 0 ? (
          (beleg.doc_type === "gutschrift" || (beleg.supplier === "HILTI" && beleg.doc_type === "rechnung")) && canManageCostCenters ? (
            <Link to={`${detailHref}#kostenstelle-pruefen`} state={{ from: fromPath, belegList, belegListOpen, belegListTotal, belegListFilters }} className="text-xs text-amber-700 hover:underline">
              {missingContext.toLocaleString("de-AT")} {missingContext === 1 ? "Zuordnung" : "Zuordnungen"} offen →
            </Link>
          ) : <span className="text-xs text-amber-700">{missingContext.toLocaleString("de-AT")} {missingContext === 1 ? "Zuordnung" : "Zuordnungen"} offen</span>
        ) : (
          <span className="text-xs text-slate-600">zugeordnet</span>
        )}
      </td>
      {showPriceReview ? <td className="px-3 py-1.5">
        {beleg.doc_type === "einzelauftrag" ? (
          <ConfirmationStatus status={beleg.confirmation_status ?? "open"} />
        ) : beleg.doc_type === "angebot" ? (
          <span className="text-xs text-slate-600">Vorläufiger Vergleich</span>
        ) : <div className="flex flex-col items-start gap-1">
          <BelegReviewStates
            beleg={beleg}
            href={`${detailHref}#preisauffaelligkeiten`}
            from={fromPath}
            emptyLabel="–"
          />
          {commentCount > 0 ? (
            <Link
              to={`${detailHref}#preisauffaelligkeiten`}
              state={{ from: fromPath }}
              aria-label={`${formatCommentCount(commentCount)} öffnen`}
              className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200 hover:text-slate-900"
            >
              {formatCommentCount(commentCount)}
            </Link>
          ) : null}
        </div>}
        {canClaim ? (
          <div className="mt-1 text-xs">
            {beleg.claim_user_id && !claimedBySelf ? (
              <span className="text-slate-600" title={beleg.claim_user_label ?? undefined}>
                Zuständig: {beleg.claim_user_label ?? "andere Person"}
              </span>
            ) : (
              <button type="button" disabled={claimPending}
                className="font-medium text-emerald-700 hover:underline disabled:opacity-50"
                onClick={() => onClaim(claimedBySelf ? "release" : "claim")}>
                {claimPending ? "Wird gespeichert …" : claimedBySelf ? "Mir zugewiesen · lösen" : "Mir zuweisen"}
              </button>
            )}
          </div>
        ) : null}
      </td> : null}
      {showPriceReview ? (
      <td className="px-3 py-1.5 text-right tabular-nums">
        {openOverpay != null && openOverpay > 0 ? (
          <Link
            to={`${detailHref}#preisauffaelligkeiten`}
            state={{ from: fromPath }}
            aria-label={`${formatEuro(openOverpay)} offene Mehrkosten in ${docLabel} prüfen`}
            className="font-medium text-rose-700 hover:underline"
          >
            {formatEuro(openOverpay)}
          </Link>
        ) : (
          <span className="text-slate-400">–</span>
        )}
      </td>
      ) : null}
      <td
        className={`px-3 py-1.5 text-right tabular-nums font-medium ${
          (toNumber(beleg.total_amount) ?? 0) < 0
            ? "text-rose-700"
            : "text-slate-900"
        }`}
      >
        {formatEuro(beleg.total_amount)}
      </td>
    </tr>
  );
}

const CONFIRMATION_STATUS: Record<NonNullable<BelegRow["confirmation_status"]>, { label: string; tone: string }> = {
  open: { label: "Prüfung erforderlich", tone: "bg-amber-50 text-amber-800" },
  query_required: { label: "Rückfrage erforderlich", tone: "bg-sky-50 text-sky-800" },
  approved: { label: "Freigegeben", tone: "bg-emerald-50 text-emerald-800" },
  rejected: { label: "Nicht freigegeben", tone: "bg-rose-50 text-rose-800" },
};

function ConfirmationStatus({ status }: { status: NonNullable<BelegRow["confirmation_status"]> }) {
  const display = CONFIRMATION_STATUS[status];
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${display.tone}`}>{display.label}</span>;
}

function OpenOverpayHeadCell({
  sort,
  onChange,
}: {
  sort: BelegSort;
  onChange: (next: BelegSort) => void;
}) {
  const active = sort === "open_overpay_desc" || sort === "open_overpay_asc";
  const ascending = sort === "open_overpay_asc";
  const next = !active
    ? "open_overpay_desc"
    : ascending
      ? "recent_desc"
      : "open_overpay_asc";
  const indicator = active ? (ascending ? "▲" : "▼") : "↕";
  return (
    <th
      scope="col"
      aria-sort={active ? (ascending ? "ascending" : "descending") : "none"}
      title="Summe positiver Mehrkosten aus offenen Rechnungspositionen. Klärungen, erledigte Positionen und Gutschriften zählen nicht."
      className="px-3 py-2 text-right font-medium"
    >
      <button
        type="button"
        onClick={() => onChange(next)}
        aria-label={`Offene Mehrkosten sortieren: ${
          !active
            ? "höchste zuerst"
            : ascending
              ? "zurück zu neueste zuerst"
              : "niedrigste zuerst"
        }`}
        className={`inline-flex items-center justify-end gap-1 whitespace-nowrap ${
          active ? "text-rose-700" : "text-slate-500 hover:text-slate-700"
        }`}
      >
        Offene Mehrkosten
        <span aria-hidden="true" className="text-[10px] opacity-70">
          {indicator}
        </span>
      </button>
    </th>
  );
}

function formatCommentCount(count: number): string {
  return `${count.toLocaleString("de-AT")} ${
    count === 1 ? "Kommentar" : "Kommentare"
  }`;
}


function DocTypeBadge({ value }: { value: BelegDocType }) {
  return (
    <span
      style={{ overflowWrap: "anywhere" }}
      className={
        value === "gutschrift"
          ? "inline-block max-w-full rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
          : "inline-block max-w-full rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
      }
    >
      {BELEG_DOC_TYPE_LABEL[value] ?? value}
    </span>
  );
}

function topSupplierOptions(items: BelegSupplierRow[]): SupplierOption[] {
  const bySupplier = new Map<string, SupplierOption>();
  for (const item of items) {
    const supplier = item.supplier?.trim();
    if (!supplier) continue;
    const current = bySupplier.get(supplier) ?? {
      supplier,
      count: 0,
      amount: 0,
    };
    current.count += 1;
    current.amount += toNumber(item.total_amount) ?? 0;
    bySupplier.set(supplier, current);
  }
  return [...bySupplier.values()]
    .sort((a, b) => b.amount - a.amount || a.supplier.localeCompare(b.supplier))
    .slice(0, SUPPLIER_FILTER_LIMIT);
}
